import { Injectable } from '@angular/core';
import { CnEditmodeService } from './cn-editmode.service';

export interface CnReportData {
  // Clarify
  breakthrough_content: string[];
  breakthrough_dropdown: string[];
  datadump_content: string[];
  question_content: string[];
  question_dropdown: string[];
  // Ideate
  brainstorm_content: string[];
  proposed_content: any[];
  // Develop
  strengthen_content: string;
  doing_content: string;
  doing_dropdown: string;
  plus_content: string[];
  opp_content: string[];
  opp_text: string[];
  issue_content: string[];
  issue_text: string[];
  thinking_content: any[];
  selected_opp: any[];
  selected_issue: any[];
  selected_thinking: any;
  // Implement
  short_content: any[];
  mid_content: any[];
  long_content: any[];
  next_content: string[];
}

@Injectable({
  providedIn: 'root'
})
export class CnReportDataService {

  constructor(private chService: CnEditmodeService) {}

  async loadAllData(): Promise<CnReportData> {
    const email = localStorage.getItem('email');
    const baseFilter = { email: { eq: email }, type: { eq: 'process-stage' } };

    const [bt, dd, qt, bs, ip, pt, st, doing, nxt, plan] = await Promise.all([
      this.chService.api.ListFrameworks({ ...baseFilter, processTypeId: { eq: 'c1c17711-b4a3-4dcc-98b5-fe73ee0791d2' }, title: { eq: 'vision' } }),
      this.chService.api.ListFrameworks({ ...baseFilter, processTypeId: { eq: 'c1c17711-b4a3-4dcc-98b5-fe73ee0791d2' }, title: { eq: 'data-gather' } }),
      this.chService.api.ListFrameworks({ ...baseFilter, processTypeId: { eq: 'c1c17711-b4a3-4dcc-98b5-fe73ee0791d2' }, title: { eq: 'breakthrough-question' } }),
      this.chService.api.ListFrameworks({ ...baseFilter, processTypeId: { eq: '790b8a8e-9fdd-475d-9ab1-d320bfe69086' }, title: { eq: 'brainstorm' } }),
      this.chService.api.ListFrameworks({ ...baseFilter, processTypeId: { eq: '790b8a8e-9fdd-475d-9ab1-d320bfe69086' }, title: { eq: 'ideas-proposed' } }),
      this.chService.api.ListFrameworks({ ...baseFilter, processTypeId: { eq: '9d455c40-4e27-4db2-ac3f-d5743d88887e' }, title: { eq: 'point' } }),
      this.chService.api.ListFrameworks({ ...baseFilter, processTypeId: { eq: '9d455c40-4e27-4db2-ac3f-d5743d88887e' }, title: { eq: 'strengthen-solution' } }),
      this.chService.api.ListFrameworks({ ...baseFilter, processTypeId: { eq: '9d455c40-4e27-4db2-ac3f-d5743d88887e' }, title: { eq: 'myself-doing' } }),
      this.chService.api.ListFrameworks({ ...baseFilter, processTypeId: { eq: 'addec506-467b-4832-8853-b09e941ea2dc' }, title: { eq: 'next-24-hours' } }),
      this.chService.api.ListFrameworks({ ...baseFilter, processTypeId: { eq: 'addec506-467b-4832-8853-b09e941ea2dc' }, title: { eq: 'action-plan' } }),
    ]);

    const data: CnReportData = {
      breakthrough_content: [],
      breakthrough_dropdown: [],
      datadump_content: [],
      question_content: [],
      question_dropdown: [],
      brainstorm_content: [],
      proposed_content: [],
      strengthen_content: '',
      doing_content: '',
      doing_dropdown: '',
      plus_content: [],
      opp_content: [],
      opp_text: [],
      issue_content: [],
      issue_text: [],
      thinking_content: [],
      selected_opp: [],
      selected_issue: [],
      selected_thinking: '',
      short_content: [],
      mid_content: [],
      long_content: [],
      next_content: [],
    };

    // Clarify - Goal (vision)
    if (bt.items.length > 0) {
      const raw = JSON.parse(bt.items[0].content);
      for (let i = 0; i < raw.length; i++) {
        const parts = (raw[i] || '').split('\n');
        data.breakthrough_dropdown[i] = parts[0] || '';
        data.breakthrough_content[i] = parts[1] || '';
      }
    }

    // Clarify - Data
    if (dd.items.length > 0) {
      data.datadump_content = JSON.parse(dd.items[0].content);
    }

    // Clarify - Challenge Questions
    if (qt.items.length > 0) {
      const raw = JSON.parse(qt.items[0].content);
      for (let i = 0; i < raw.length; i++) {
        const parts = (raw[i] || '').split('\n');
        data.question_dropdown[i] = parts[0] || '';
        data.question_content[i] = parts[1] || '';
      }
    }

    // Ideate - Brainstorm
    if (bs.items.length > 0) {
      data.brainstorm_content = JSON.parse(bs.items[0].content);
    }

    // Ideate - Proposed/Selected Ideas
    if (ip.items.length > 0) {
      const raw = JSON.parse(ip.items[0].content);
      for (let i = 0; i < raw.length; i++) {
        if (raw[i]?.length) {
          data.proposed_content[i] = JSON.parse(raw[i]);
        }
      }
    }

    // Develop - POINT evaluation
    if (pt.items.length > 0) {
      const content = JSON.parse(pt.items[0].content);
      data.plus_content = JSON.parse(content.point.pluses);

      const opportunities = JSON.parse(content.point.opportunities);
      data.selected_opp = opportunities[0] || [];
      for (let i = 1; i < opportunities.length; i++) {
        const parts = (opportunities[i] || '').split('\n');
        data.opp_content[i] = parts[0] || '';
        data.opp_text[i] = parts[1] || '';
      }

      const issues = JSON.parse(content.point.issues);
      data.selected_issue = issues[0] || [];
      for (let i = 1; i < issues.length; i++) {
        const parts = (issues[i] || '').split('\n');
        data.issue_content[i] = parts[0] || '';
        data.issue_text[i] = parts[1] || '';
      }

      const thinkRaw = JSON.parse(content.point.new_thinking);
      for (let i = 0; i < thinkRaw.length; i++) {
        data.thinking_content[i] = JSON.parse(thinkRaw[i]);
      }
      data.selected_thinking = data.thinking_content[0] || '';
    }

    // Develop - Strengthen Solution
    if (st.items.length > 0) {
      data.strengthen_content = st.items[0].content;
    }

    // Develop - Solution Prototype (Doing)
    if (doing.items.length > 0) {
      const raw = doing.items[0].content;
      data.doing_dropdown = raw.substr(0, raw.indexOf('\n'));
      data.doing_content = raw.substr(raw.indexOf('\n') + 1);
    }

    // Implement - Next 24 Hours
    if (nxt.items.length > 0) {
      data.next_content = JSON.parse(nxt.items[0].content);
    }

    // Implement - Action Plan
    if (plan.items.length > 0) {
      const all = JSON.parse(plan.items[0].content);
      data.short_content = JSON.parse(all.short_term);
      data.mid_content = JSON.parse(all.mid_term);
      data.long_content = JSON.parse(all.long_term);
    }

    return data;
  }
}
